import * as THREE from 'three';
import { CORVIN } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/** Ser Corvin's own effects: his footfalls and cleave, the challenge's thread, the charge's line and end, the Night. */
const TAU = Math.PI * 2;

export const corvinFx: UnRender = {
  look: { glow: '#ff6a3a', accent: '#ff4f3a', pale: '#ff8a6e', dark: '#121318', smoke: '#15161c' },
  cone: { radius: CORVIN.night.radius, half: CORVIN.night.half },
  clips: { corvinChallenge: 'challenge' },
  frame: (c, s, e, p, yaw, _h) => {
    const u = e.un;
    if (!u) return;
    const beat = (s.tick + e.id) % 12;
    const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    if (e.anim === 'walk' && u.busy === 0 && beat % 6 === 0) {
      const side = beat % 12 === 0 ? 1 : -1;
      c.vfx.ring(p.clone().add(new THREE.Vector3(fwd.z * side * 0.3, 0.02, -fwd.x * side * 0.3)), 0.05, 0.36, '#ff4f3a', 12, 0.6);
    }
    // Charging: a wake of embers and dust behind him.
    if (u.busy > 0) {
      c.vfx.burst(p.clone().setY(p.y + 0.2), { n: c.low ? 2 : 5, color: '#ff6a3a', speed: 0.04, up: 0.05, size: 0.1, life: 8, gravity: 0.006 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: c.low ? 1 : 2, color: '#2b2422', speed: 0.03, up: 0.02, size: 0.5, life: 12, smoke: true, spread: 0.25 });
    }
    if (e.anim === 'attack' && e.animT === 2 && u.cast === 0) {
      // The cleave lands in front of him.
      const hit = p.clone().addScaledVector(fwd, 0.9);
      c.vfx.ring(hit, 0.1, 1.0, '#ff4f3a', 7, 1);
      c.vfx.burst(hit.clone().setY(hit.y + 0.2), { n: c.low ? 4 : 9, color: '#ff8a6e', speed: 0.07, up: 0.08, size: 0.09, life: 8, gravity: 0.012 });
      c.shake(0.1);
    }
  },
  skill: (c, _s, _e, name, p) => {
    if (name !== 'corvinCharge') return false;
    c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.6, '#ff4f3a', 10, 0.9);
    c.shake(0.12);
    return true;
  },
  beam: (c, kind, a, b) => {
    if (kind === 'corvinChallenge') {
      // A thread of ember from his blade to the hero he marks.
      c.vfx.beam(a.clone().setY(a.y + 0.6), b, '#ff6a3a', 10, 0.035, 0.3);
      c.vfx.beam(a, b, '#ffd2b0', 6, 0.015, 0);
      c.flare(b.clone().setY(b.y + 0.6), '#ff4f3a', 1.4, 10);
      c.vfx.ring(b.clone().setY(b.y - 0.55), 0.2, 0.7, '#ff4f3a', 12, 0.9);
      return true;
    }
    if (kind === 'corvinCharge') {
      // The line he will run along, scorched for a moment.
      const g1 = a.clone().setY(a.y - 0.75);
      const g2 = b.clone().setY(b.y - 0.55);
      c.vfx.shaft(g1, g2, '#ff4f3a', 8, 0.5);
      c.vfx.beam(g1, g2, '#ff8a6e', 6, 0.04, 0.2);
      c.vfx.burst(g1, { n: c.low ? 4 : 10, color: '#2b2422', speed: 0.05, up: 0.03, size: 0.5, life: 14, smoke: true, spread: 0.3 });
      return true;
    }
    return false;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'corvinImpact') {
      // Dust, not red: the charge hurt along its path, not here (red on the floor is where a blow lands).
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, Math.max(1.2, r), '#c4ccd8', 10, 0.7);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: '#4a3d33', speed: 0.07, up: 0.05, size: 0.5, life: 16, smoke: true, spread: 0.4 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 14, color: '#ff8a6e', speed: 0.1, up: 0.08, size: 0.09, life: 10, gravity: 0.012 });
      c.shake(0.35);
      return true;
    }
    if (kind !== 'corvinNight') return false;
    // The Night of the Gate: a wall of shadow sweeps out over the cone, embers and black smoke on its front.
    if (e) c.force(e.id, 'night');
    const a = e?.un ? e.un.castA : e?.facing ?? 0;
    const th = (a / 1024) * TAU;
    const half = (CORVIN.night.half / 1024) * TAU;
    const R = (CORVIN.night.radius / 1000) * WORLD_PER_HEX;
    const steps = low ? 5 : 9;
    for (let i = 0; i < steps; i++) {
      const t = th - half + (2 * half * i) / (steps - 1);
      for (const k of [0.45, 0.8, 1]) {
        const q = p.clone().add(new THREE.Vector3(Math.cos(t) * R * k, 0.3, Math.sin(t) * R * k));
        c.vfx.burst(q, { n: low ? 2 : 4, color: '#0f1015', speed: 0.05, up: 0.06, size: 0.9, life: 26, smoke: true, spread: 0.3 });
        if (k === 1) c.vfx.burst(q, { n: low ? 2 : 5, color: '#ff4f3a', speed: 0.08, up: 0.1, size: 0.12, life: 14, gravity: 0.008 });
      }
    }
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, R, '#ff4f3a', 14, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, R * 0.6, '#1a1015', 18, 0.8);
    c.flare(p.clone().setY(p.y + 1), '#ff4f3a', 7, 14);
    c.shake(0.9);
    return true;
  },
};
